import { ValidationControlBase } from "@frui.ts/bootstrap";
import type { CommonInputProps } from "@frui.ts/bootstrap/dist/controls/commonInputProps";
import type { PropertyName } from "@frui.ts/helpers";
import { bound, ensureObservableProperty } from "@frui.ts/helpers";
import type { IBindingProps } from "@frui.ts/views";
import { get, isObservable, isObservableProp } from "mobx";
import * as React from "react";
import type { FormCheckProps } from "react-bootstrap";
import { Form } from "react-bootstrap";

export interface CheckArrayProps {
  isNumeric?: boolean;
  className?: string;
}

export class CheckArray<TTarget> extends ValidationControlBase<
  TTarget,
  CheckArrayProps & FormCheckProps & CommonInputProps & IBindingProps<TTarget>
> {
  protected get inheritedProps() {
    const { id, noValidation, errorMessage, className, target, property, onValueChanged, isNumeric, ...otherProps } = this.props;

    return otherProps;
  }

  @bound protected renderInner() {
    const { className, id, property } = this.props;
    const validationError = this.getValidationError();

    return (
      <Form.Check
        {...this.inheritedProps}
        id={`${property}-${id}`}
        className={`check-array ${className ?? ""}`}
        checked={!!this.value}
        onChange={this.handleValueChanged}
        isInvalid={!!validationError}
      />
    );
  }

  protected get value() {
    const { target, id, isNumeric } = this.props;
    const property = this.props.property as PropertyName<TTarget>;

    if (!target) {
      //// throw new Error("'target' prop has not been set");
      // tslint:disable-next-line: no-console
      console.warn("'target' prop has not been set");
      return undefined;
    }
    if (!property) {
      throw new Error("'property' prop has not been set");
    }

    if (!isObservable(target) || !isObservableProp(target, property)) {
      ensureObservableProperty(target, property, target[property as keyof TTarget]);
    }

    if (!id) {
      throw new Error("'id' prop is required for CheckBoxArray!");
    }

    let value: string | number | undefined = id;

    if (isNumeric) {
      value = parseInt(id, 10);
    }

    return (get(target, property) || []).indexOf(value) >= 0;
  }

  @bound protected handleValueChanged(e: React.FormEvent<any>) {
    const { id, isNumeric } = this.props;
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string };
    let value: undefined | string | number;

    if (isNumeric && id) {
      value = parseInt(id, 10);
    } else {
      value = id;
    }

    const array: Array<string | number> = (get(target, property) || []).slice(0);

    if (value === undefined) {
      return;
    }

    const index = array.indexOf(value);

    // Remove or add?
    if (index >= 0) {
      array.splice(index, 1);
    } else {
      array.push(value);
    }

    this.setValue(array);
  }
}
